import { useTranslations } from '@vocab/react';
import { Stack, Box, Disclosure } from 'braid-design-system';
import type { FC } from 'react';

import {
  STATUS_CERTIFIED,
  STATUS_CUSTOM,
  STATUS_MATCH,
} from 'src/constants/roleRequirementStatus';

import translations from '../.vocab';

import { RoleRequirement, type RoleRequirementProps } from './RoleRequirement';

interface RoleRequirementsDisclosureProps {
  roleRequirements: Array<RoleRequirementProps['roleRequirement']>;
  jobApplicationId: string;
  isMobile: boolean;
  isExpanded: boolean;
  toggleIsExpanded: () => void;
  isFilterExpand: boolean;
}

export const RoleRequirementsDisclosure: FC<
  RoleRequirementsDisclosureProps
> = ({
  roleRequirements,
  jobApplicationId,
  isMobile,
  isExpanded,
  toggleIsExpanded,
  isFilterExpand,
}) => {
  const { t } = useTranslations(translations);

  const {
    customQuestionsCount,
    libraryQuestionsCount,
    matchingLibraryQuestionsCount,
  } = roleRequirements.reduce(
    (counts, { status }) => {
      if (status === STATUS_CUSTOM) {
        counts.customQuestionsCount = counts.customQuestionsCount + 1;
      } else {
        counts.libraryQuestionsCount = counts.libraryQuestionsCount + 1;
      }

      if (status === STATUS_CERTIFIED) {
        counts.verifiedAnswersCount = counts.verifiedAnswersCount + 1;
        counts.matchingLibraryQuestionsCount =
          counts.matchingLibraryQuestionsCount + 1;
      } else if (status === STATUS_MATCH) {
        counts.matchingLibraryQuestionsCount =
          counts.matchingLibraryQuestionsCount + 1;
      }
      return counts;
    },
    {
      matchingLibraryQuestionsCount: 0,
      libraryQuestionsCount: 0,
      verifiedAnswersCount: 0,
      customQuestionsCount: 0,
    },
  );

  const hasOnlyCustomQuestions =
    libraryQuestionsCount === 0 && customQuestionsCount > 0;

  const label = hasOnlyCustomQuestions
    ? t('Screening questions')
    : t('Screening questions (matchingQuestions/totalQuestions match)', {
        matchingQuestions: String(matchingLibraryQuestionsCount),
        totalQuestions: String(libraryQuestionsCount),
      });

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Disclosure
        id={`${jobApplicationId}-rr`}
        expandLabel={label}
        size="small"
        expanded={isExpanded}
        onToggle={toggleIsExpanded}
        weight="weak" // FIXME: NVL - remove this
      >
        <Box paddingTop="small">
          {/* FIXME: NVL - change to small */}
          <Stack space="medium">
            {roleRequirements.map((rr) => (
              <RoleRequirement
                key={rr.question}
                isMobile={isMobile}
                jobApplicationId={jobApplicationId}
                roleRequirement={rr}
                hasOnlyCustomQuestions={hasOnlyCustomQuestions}
                isFilterExpand={isFilterExpand}
              />
            ))}
          </Stack>
        </Box>
      </Disclosure>
    </Box>
  );
};
